import * as TabsPrimitive from '@radix-ui/react-tabs'
import * as React from 'react'
import { twMerge } from 'tailwind-merge'
import { tv } from 'tailwind-variants'

const tabsVariants = tv({
  slots: {
    list: '',
    trigger: '',
  },
  variants: {
    variant: {
      normal: {
        list: 'inline-flex h-full flex-wrap items-center justify-center gap-4 rounded-t-lg bg-white p-1 lg:h-9',
        trigger:
          'inline-flex items-center justify-center whitespace-nowrap rounded-md px-5 py-1 text-sm font-medium text-bios-secondary/50 transition-all hover:font-bold hover:text-bios-secondary hover:shadow-sm focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-gray-200/50 data-[state=active]:font-bold data-[state=active]:text-bios-secondary data-[state=active]:shadow-sm',
      },
      secondary: {
        list: 'w-full h-max flex flex-wrap justify-center items-center lg:flex-nowrap',
        trigger:
          'pb-10 pt-10 px-6 border-b-4 border-white group data-[state=active]:border-bios-primary lg:pt-0 lg:w-full lg-px-0',
      },
    },
  },
  defaultVariants: {
    variant: 'normal',
  },
})

type TabsVariant = 'normal' | 'secondary'

interface TabsContextValue {
  variant: TabsVariant
}

const TabsContext = React.createContext<TabsContextValue | undefined>(undefined)

const useTabsContext = () => {
  const context = React.useContext(TabsContext)
  if (!context) {
    throw new Error('Tabs components must be used within a Tabs component')
  }
  return context
}

interface TabsProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root> {
  variant?: TabsVariant
}

const Tabs = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Root>,
  TabsProps
>(({ variant = 'normal', children, ...props }, ref) => (
  <TabsContext.Provider value={{ variant }}>
    <TabsPrimitive.Root ref={ref} {...props}>
      {children}
    </TabsPrimitive.Root>
  </TabsContext.Provider>
))
Tabs.displayName = 'Tabs'

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => {
  const { variant } = useTabsContext()

  return (
    <TabsPrimitive.List
      ref={ref}
      className={twMerge(tabsVariants({ variant }).list(), className)}
      {...props}
    />
  )
})
TabsList.displayName = TabsPrimitive.List.displayName

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => {
  const { variant } = useTabsContext()

  return (
    <>
      {variant === 'normal' && (
        <TabsPrimitive.Trigger
          ref={ref}
          className={twMerge(tabsVariants({ variant }).trigger(), className)}
          {...props}
        />
      )}
      {variant === 'secondary' && (
        <TabsPrimitive.Trigger
          ref={ref}
          className={twMerge(tabsVariants({ variant }).trigger(), className)}
          {...props}
        />
      )}
    </>
  )
})
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={twMerge(
      'rounded-b-lg bg-white p-5 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2',
      className,
    )}
    {...props}
  />
))
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsList, TabsTrigger, TabsContent }
